import { useCallback, useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const CARD = 'rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6';
const LABEL = 'block text-xs font-bold uppercase tracking-[0.12em] text-[#d2c5ab]';
const INPUT =
  'mt-2 w-full rounded border border-[#4e4632] bg-[#353534] px-3 py-2 text-sm text-[#e5e2e1] outline-none focus:border-[#ffcc00]';
const PRIMARY =
  'rounded-lg bg-[#ffcc00] px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-[#3d2f00] transition hover:brightness-110 disabled:opacity-50';
const OUTLINE =
  'rounded-lg border border-[#ffcc00] px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] text-[#ffcc00] transition hover:bg-[#ffcc00] hover:text-[#3d2f00] disabled:opacity-50';

export default function SmsGateway() {
  const [settings, setSettings] = useState(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [sender, setSender] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/sms-gateway');
      const s = data.settings || {};
      setSettings(s);
      setBaseUrl(s.baseUrl || '');
      setSender(s.sender || '');
      setEnabled(Boolean(s.enabled));
      setApiKey('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load SMS gateway settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const body = { baseUrl: baseUrl.trim(), sender: sender.trim(), enabled };
      // Only send the key if the admin typed a new one; blank leaves it unchanged.
      if (apiKey.trim()) body.apiKey = apiKey.trim();
      const { data } = await api.put('/admin/sms-gateway', body);
      setSettings(data.settings || null);
      setApiKey('');
      setNotice('Saved.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm('Remove the stored Taban SMS API key?')) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { data } = await api.put('/admin/sms-gateway', { apiKey: null });
      setSettings(data.settings || null);
      setApiKey('');
      setNotice('API key removed.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not remove the key.');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) {
      setError('Enter a phone number to send a test to.');
      return;
    }
    setTesting(true);
    setError('');
    setNotice('');
    try {
      const { data } = await api.post('/admin/sms-gateway/test', { phone: testPhone.trim() });
      setNotice(
        data.test
          ? 'Test accepted by Taban SMS (test key — no real SMS sent).'
          : `Test SMS sent${data.messageId ? ` (id ${data.messageId})` : ''}.`
      );
    } catch (err) {
      const reason = err?.response?.data?.reason || err?.response?.data?.error;
      setError(
        reason === 'sms_not_configured'
          ? 'SMS is not configured. Enable the gateway and save an API key first.'
          : `Test failed: ${reason || 'unknown error'}.`
      );
    } finally {
      setTesting(false);
    }
  };

  // Test vs Live is implied by the key prefix (tb_test_ / tb_live_); the
  // masked hint keeps its first four characters, so "tb_l…"/"tb_t…" is enough.
  const hint = (settings?.apiKeyHint || '').toLowerCase();
  const mode = settings?.hasApiKey
    ? hint.startsWith('tb_l')
      ? 'Live'
      : hint.startsWith('tb_t')
      ? 'Test'
      : null
    : null;

  return (
    <Layout searchPlaceholder="SMS gateway settings...">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="text-xl font-bold text-[#f3efe7]">SMS Gateway</h1>
          <p className="mt-1 text-sm text-[#d2c5ab]">
            Connect Shimbirr phone and registration verification to the Taban SMS hosted service.
            Request a developer key at{' '}
            <a
              href="https://docs.sms.taban.so"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-[#ffcc00] underline"
            >
              docs.sms.taban.so
            </a>
            , then enter it below. A <code className="text-[#ffcc00]">tb_test_</code> key
            simulates delivery; a <code className="text-[#ffcc00]">tb_live_</code> key sends real SMS.
          </p>
        </div>

        {error ? (
          <div className="rounded-lg border border-[#93000a] bg-[#2a1112] px-4 py-3 text-sm text-[#ffdad6]">
            {error}
          </div>
        ) : null}
        {notice ? (
          <div className="rounded-lg border border-[#2f5d2f] bg-[#122712] px-4 py-3 text-sm text-[#c9f2c9]">
            {notice}
          </div>
        ) : null}

        {loading ? (
          <div className={`${CARD} text-sm text-[#d2c5ab]`}>Loading…</div>
        ) : (
          <>
            <div className={`${CARD} space-y-5`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className={LABEL}>Gateway status</span>
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[#e5e2e1]">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                        enabled
                          ? 'bg-[#122712] text-[#c9f2c9]'
                          : 'bg-[#353534] text-[#9c927c]'
                      }`}
                    >
                      {enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {mode ? (
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                          mode === 'Live'
                            ? 'bg-[#fd6c00] text-[#241a00]'
                            : 'bg-[#ffcc00] text-[#3d2f00]'
                        }`}
                      >
                        {mode} key
                      </span>
                    ) : null}
                    <span className="text-[#9c927c]">
                      {settings?.hasApiKey ? settings.apiKeyHint : 'no key set'}
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEnabled((v) => !v)}
                  className={enabled ? PRIMARY : OUTLINE}
                >
                  {enabled ? 'On' : 'Off'}
                </button>
              </div>

              <div>
                <label className={LABEL}>API base URL</label>
                <input
                  className={INPUT}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://api.sms.taban.so/api/sms/v1"
                />
              </div>

              <div>
                <label className={LABEL}>Sender (optional)</label>
                <input
                  className={INPUT}
                  value={sender}
                  onChange={(e) => setSender(e.target.value)}
                  placeholder="Shimbirr"
                />
              </div>

              <div>
                <label className={LABEL}>
                  API key {settings?.hasApiKey ? `(stored: ${settings.apiKeyHint})` : ''}
                </label>
                <input
                  className={INPUT}
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={settings?.hasApiKey ? 'Leave blank to keep current key' : 'tb_live_… or tb_test_…'}
                  autoComplete="off"
                />
                <p className="mt-1 text-xs text-[#9c927c]">
                  The key is stored securely and never shown again in full.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button type="button" onClick={save} disabled={saving} className={PRIMARY}>
                  {saving ? 'Saving…' : 'Save settings'}
                </button>
                {settings?.hasApiKey ? (
                  <button type="button" onClick={clearKey} disabled={saving} className={OUTLINE}>
                    Remove key
                  </button>
                ) : null}
              </div>
            </div>

            <div className={`${CARD} space-y-4`}>
              <div>
                <span className={LABEL}>Send a test SMS</span>
                <p className="mt-1 text-xs text-[#9c927c]">
                  Sends one message through the gateway to confirm the key works.
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1">
                  <label className={LABEL}>Phone number</label>
                  <input
                    className={INPUT}
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+2526XXXXXXX"
                  />
                </div>
                <button type="button" onClick={sendTest} disabled={testing} className={OUTLINE}>
                  {testing ? 'Sending…' : 'Send test'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
